<!--
  Thread — Phase 4-3 / T14.

  Renders a conversation's message list. Groups messages into bursts (same
  sender within 2 minutes) and inserts day-divider chips between calendar
  days. Manages scroll preservation: auto-bottom when user is near the foot,
  position-anchor when user has scrolled up to read history.
-->
<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { conversationMessages, type Message } from '../lib/api/queries.js';
  import { tick } from 'svelte';
  import MessageBubble from './MessageBubble.svelte';
  import { typing } from '../lib/stores/typing.svelte.js';

  type Props = { conversationId: string; customerDisplayName?: string | null };
  let { conversationId, customerDisplayName = null }: Props = $props();

  const q = $derived(createQuery(conversationMessages(conversationId)));
  // Reactive — re-evaluates whenever typing.set / typing.clear mutates the
  // store's $state map. App.svelte feeds it from `conversation.typing`
  // realtime frames.
  const isTyping = $derived(typing.isTyping(conversationId));

  type DisplayItem =
    | { kind: 'divider'; label: string; key: string }
    | {
        kind: 'message';
        message: Message;
        isFirstOfBurst: boolean;
        isLastOfBurst: boolean;
        senderName?: string;
        key: string;
      };

  const items = $derived<DisplayItem[]>(buildDisplayItems($q.data ?? []));

  function buildDisplayItems(messages: Message[]): DisplayItem[] {
    if (messages.length === 0) return [];
    const sorted = [...messages].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    const out: DisplayItem[] = [];
    let lastDay = '';
    for (let i = 0; i < sorted.length; i++) {
      const msg = sorted[i]!;
      const dayKey = new Date(msg.createdAt).toDateString();
      if (dayKey !== lastDay) {
        out.push({ kind: 'divider', label: dayLabel(msg.createdAt), key: `d-${dayKey}` });
        lastDay = dayKey;
      }
      const prev = sorted[i - 1];
      const next = sorted[i + 1];
      const isFirstOfBurst = !prev || !sameBurst(prev, msg);
      const isLastOfBurst = !next || !sameBurst(msg, next);
      out.push({
        kind: 'message',
        message: msg,
        isFirstOfBurst,
        isLastOfBurst,
        senderName: isFirstOfBurst
          ? msg.direction === 'inbound'
            ? inboundSenderName(msg)
            : outboundSenderName(msg)
          : undefined,
        key: msg.id,
      });
    }
    return out;
  }

  function sameBurst(a: Message, b: Message): boolean {
    if (a.direction !== b.direction) return false;
    const at = new Date(a.createdAt).getTime();
    const bt = new Date(b.createdAt).getTime();
    if (Number.isNaN(at) || Number.isNaN(bt)) return false;
    return Math.abs(bt - at) < 2 * 60_000;
  }

  function inboundSenderName(_m: Message): string {
    // Prefer the conversation-level customer name (populated from Meta profile
    // first_name/last_name when the customer was first seen). Falls back to
    // a generic label if profile lookup hadn't completed yet.
    const trimmed = customerDisplayName?.trim();
    if (trimmed) return trimmed;
    return 'Customer';
  }

  function outboundSenderName(m: Message): string {
    const trimmed = m.authorDisplayName?.trim();
    if (trimmed) return trimmed;
    return 'Operator';
  }

  function dayLabel(iso: string): string {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  // Scroll preservation
  let scrollEl: HTMLDivElement | null = $state(null);
  let lastCount = 0;
  let stickToBottom = true;

  function handleScroll() {
    if (!scrollEl) return;
    const distance = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
    stickToBottom = distance < 80;
  }

  $effect(() => {
    const count = items.length;
    if (count === lastCount) return;
    const grew = count > lastCount;
    lastCount = count;
    if (!scrollEl || !grew) return;
    const el = scrollEl;
    if (stickToBottom) {
      void tick().then(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  });

  function retryMessage(_id: string) {
    // Real retry is wired by Composer in T15 via mutation key reuse; until then,
    // just invalidate so the failed-state bubble reloads from server state.
    void $q.refetch();
  }
</script>

<div
  class="thread"
  bind:this={scrollEl}
  onscroll={handleScroll}
  role="log"
  aria-live="polite"
  aria-label="Messages"
>
  {#if $q.isPending}
    <div class="status">Loading messages...</div>
  {:else if $q.error}
    <div class="status error">Could not load messages. <button onclick={() => $q.refetch()}>Try again</button></div>
  {:else if items.length === 0}
    <div class="status">No messages in this conversation yet.</div>
  {:else}
    {#each items as item (item.key)}
      {#if item.kind === 'divider'}
        <div class="divider"><span class="chip">{item.label}</span></div>
      {:else}
        <MessageBubble
          message={item.message}
          isFirstOfBurst={item.isFirstOfBurst}
          isLastOfBurst={item.isLastOfBurst}
          senderName={item.senderName}
          status={item.message.direction === 'outbound' ? 'SENT' : undefined}
          onRetry={retryMessage}
        />
      {/if}
    {/each}
  {/if}
  {#if isTyping && items.length > 0}
    <div class="typing" aria-live="polite">Customer is typing…</div>
  {/if}
</div>

<style>
  .thread {
    display: flex;
    flex-direction: column;
    padding: 16px 14px;
    height: 100%;
    overflow-y: auto;
    background: var(--widget-bg, oklch(99% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
  }

  .status {
    text-align: center;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 13px;
    padding: 20px;
  }
  .status.error button {
    background: none;
    border: none;
    color: var(--widget-accent, oklch(58% 0.18 260));
    font-weight: 600;
    cursor: pointer;
    padding: 0 4px;
    text-decoration: underline;
  }

  .divider {
    display: flex;
    justify-content: center;
    margin: 20px 0 14px;
  }
  .divider .chip {
    padding: 4px 10px;
    border-radius: 999px;
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 4%, transparent);
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  /*
   * Customer-typing pill — DESIGN.md alignment: italic body text, muted
   * color, no spinner, fade-in only. Renders below the last bubble and
   * inside the scroll container so it auto-scrolls into view if the user
   * is anchored to the bottom.
   */
  .typing {
    margin: 6px 4px 2px;
    padding: 2px 8px;
    font-size: 12.5px;
    font-style: italic;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    animation: typing-fade-in 160ms ease-out;
  }
  @keyframes typing-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
</style>
